import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  ChevronDown,
  Italic,
  Strikethrough,
  Underline,
} from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { LABEL_COLORS } from "../../nodes/shared/CanvasColorPalettes";
import { CanvasColorPickerButton } from "../../nodes/shared/CanvasColorPickerButton";
import type { CanvasListType } from "./canvasListCommands";
import { CanvasToolbarListMenu } from "./CanvasToolbarListMenu";
import {
  CanvasToolbarButton,
  CanvasToolbarDivider,
  CanvasToolbarGroup,
  canvasToolbarButtonVariants,
} from "./index";

interface CanvasToolbarFormatMenuProps {
  isBold?: boolean;
  isItalic?: boolean;
  isUnderline?: boolean;
  isStrikethrough?: boolean;
  textColor?: string;
  textColors?: typeof LABEL_COLORS;
  onBoldChange: () => void;
  onItalicChange?: () => void;
  onUnderlineChange?: () => void;
  onStrikethroughChange?: () => void;
  onTextColorChange?: (color: string) => void;
  onListTypeSelect?: (type: CanvasListType) => void;
  onOpenChange?: (open: boolean) => void;
}

export function CanvasToolbarFormatMenu({
  isBold = false,
  isItalic = false,
  isUnderline = false,
  isStrikethrough = false,
  textColor,
  textColors = LABEL_COLORS,
  onBoldChange,
  onItalicChange,
  onUnderlineChange,
  onStrikethroughChange,
  onTextColorChange,
  onListTypeSelect,
  onOpenChange,
}: CanvasToolbarFormatMenuProps) {
  const [open, setOpen] = useState(false);

  // Refs updated before notify — nested menus must not OR a stale `open` from
  // the render that created their callback (that re-latches the parent toolbar
  // after Format has already closed).
  const openRef = useRef(false);
  const isColorOpenRef = useRef(false);
  const isListOpenRef = useRef(false);

  const notifyOpen = useCallback(() => {
    onOpenChange?.(
      openRef.current || isColorOpenRef.current || isListOpenRef.current,
    );
  }, [onOpenChange]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      openRef.current = next;
      if (!next) {
        isColorOpenRef.current = false;
        isListOpenRef.current = false;
      }
      setOpen(next);
      notifyOpen();
    },
    [notifyOpen],
  );

  const handleColorOpen = useCallback(
    (colorOpen: boolean) => {
      if (!openRef.current && colorOpen) return;
      isColorOpenRef.current = colorOpen;
      notifyOpen();
    },
    [notifyOpen],
  );

  const handleListOpen = useCallback(
    (listOpen: boolean) => {
      if (!openRef.current && listOpen) return;
      isListOpenRef.current = listOpen;
      notifyOpen();
    },
    [notifyOpen],
  );

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              className={cn(
                canvasToolbarButtonVariants({
                  active: open,
                  hasDropdown: true,
                }),
              )}
              onClick={(e) => e.stopPropagation()}
            >
              <Bold className="size-4" />
              <ChevronDown className="size-2.5" strokeWidth={2.5} />
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="top">Formatting</TooltipContent>
      </Tooltip>
      <PopoverContent
        className="w-auto border-0 bg-transparent p-0 shadow-none"
        align="center"
        side="top"
        sideOffset={8}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="inline-flex flex-nowrap items-center gap-0.5 rounded-lg border border-n-10 bg-n-00 p-1 shadow-elev-5">
          <CanvasToolbarGroup>
            <CanvasToolbarButton
              label="Bold"
              active={isBold}
              onClick={(e) => {
                e.stopPropagation();
                onBoldChange();
              }}
            >
              <Bold className="size-4" />
            </CanvasToolbarButton>
            {onItalicChange && (
              <CanvasToolbarButton
                label="Italic"
                active={isItalic}
                onClick={(e) => {
                  e.stopPropagation();
                  onItalicChange();
                }}
              >
                <Italic className="size-4" />
              </CanvasToolbarButton>
            )}
            {onUnderlineChange && (
              <CanvasToolbarButton
                label="Underline"
                active={isUnderline}
                onClick={(e) => {
                  e.stopPropagation();
                  onUnderlineChange();
                }}
              >
                <Underline className="size-4" />
              </CanvasToolbarButton>
            )}
            {onStrikethroughChange && (
              <CanvasToolbarButton
                label="Strikethrough"
                active={isStrikethrough}
                onClick={(e) => {
                  e.stopPropagation();
                  onStrikethroughChange();
                }}
              >
                <Strikethrough className="size-4" />
              </CanvasToolbarButton>
            )}
            {onTextColorChange && (
              <CanvasColorPickerButton
                colors={textColors}
                activeValue={textColor ?? "default"}
                tooltip="Text color"
                onColorChange={onTextColorChange}
                onOpenChange={handleColorOpen}
              />
            )}
          </CanvasToolbarGroup>
          {onListTypeSelect && (
            <>
              <CanvasToolbarDivider />
              <CanvasToolbarGroup>
                <CanvasToolbarListMenu
                  onListTypeSelect={onListTypeSelect}
                  onOpenChange={handleListOpen}
                />
              </CanvasToolbarGroup>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface CanvasToolbarAlignMenuProps {
  alignment?: "left" | "center" | "right";
  onAlignChange: (align: "left" | "center" | "right") => void;
  onOpenChange?: (open: boolean) => void;
}

export function CanvasToolbarAlignMenu({
  alignment = "left",
  onAlignChange,
  onOpenChange,
}: CanvasToolbarAlignMenuProps) {
  const [open, setOpen] = useState(false);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
  };

  const AlignIcon =
    alignment === "center"
      ? AlignCenter
      : alignment === "right"
        ? AlignRight
        : AlignLeft;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              className={cn(
                canvasToolbarButtonVariants({
                  active: open,
                  hasDropdown: true,
                }),
              )}
              onClick={(e) => e.stopPropagation()}
            >
              <AlignIcon className="size-4" />
              <ChevronDown className="size-2.5" strokeWidth={2.5} />
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="top">Align</TooltipContent>
      </Tooltip>
      <PopoverContent
        className="w-auto border-0 bg-transparent p-0 shadow-none"
        align="center"
        side="top"
        sideOffset={8}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="inline-flex items-center gap-0.5 rounded-lg border border-n-10 bg-n-00 p-1 shadow-elev-5">
          <CanvasToolbarGroup>
            <CanvasToolbarButton
              label="Align left"
              active={alignment === "left"}
              onClick={() => {
                onAlignChange("left");
                handleOpenChange(false);
              }}
            >
              <AlignLeft className="size-4" />
            </CanvasToolbarButton>
            <CanvasToolbarButton
              label="Align center"
              active={alignment === "center"}
              onClick={() => {
                onAlignChange("center");
                handleOpenChange(false);
              }}
            >
              <AlignCenter className="size-4" />
            </CanvasToolbarButton>
            <CanvasToolbarButton
              label="Align right"
              active={alignment === "right"}
              onClick={() => {
                onAlignChange("right");
                handleOpenChange(false);
              }}
            >
              <AlignRight className="size-4" />
            </CanvasToolbarButton>
          </CanvasToolbarGroup>
        </div>
      </PopoverContent>
    </Popover>
  );
}
